import { useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Drawer from '@mui/material/Drawer';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';
import { useRouter, usePathname } from 'src/routes/hooks';

import { useResponsive } from 'src/hooks/use-responsive';

import { useAuthContext } from 'src/auth/hooks';

import Iconify from 'src/components/iconify';
import Logo from 'src/components/logo/logo-app';
import Scrollbar from 'src/components/scrollbar';
import { NavSectionVertical } from 'src/components/nav-section';

import { NAV } from '../config-layout';
import { useNavData } from './config-navigation';
import NavToggleButton from '../common/nav-toggle-button';

// ----------------------------------------------------------------------

export default function NavVertical({ openNav, onCloseNav }) {
  const { logout, user } = useAuthContext();

  const router = useRouter();

  const pathname = usePathname();

  const lgUp = useResponsive('up', 'lg');

  const navData = useNavData();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleLogout = () => {
    logout();
    router.replace(paths.auth.login);
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': {
          height: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Logo sx={{ mt: 3, ml: 4, mb: 1, width: 210, height: 150 }} />

      <NavSectionVertical
        data={navData}
        slotProps={{
          currentRole: user?.currentRol?.rolName,
        }}
      />

      <Box sx={{ flexGrow: 1 }} />

      <Stack sx={{ pb: 3, pt: 5 }} direction="column">
        <Stack direction="column" alignItems="center" textAlign="center" spacing={1}>
          <Typography variant="body2">
            {user?.userInfo?.givenName} {user?.userInfo?.surname}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Rol: {user?.currentRol?.rolName?.toUpperCase()}
          </Typography>
        </Stack>
        <Button variant="outlined" color="error" onClick={handleLogout} sx={{ m: 3 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Iconify icon="mdi:logout" height={24} width={24} />
            <Typography variant="body2">Desconectarse</Typography>
          </Stack>
        </Button>
      </Stack>
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.W_VERTICAL },
      }}
    >
      <NavToggleButton />

      {lgUp ? (
        <Stack
          sx={{
            height: 1,
            position: 'fixed',
            width: NAV.W_VERTICAL,
            borderRight: (theme) => `dashed 1px ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Stack>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: {
              width: NAV.W_VERTICAL,
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

NavVertical.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};
